import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Input, Select } from 'antd';
import { getClients } from '../../services/clientService';
import { createDevice, updateDevice, getDeviceDetails } from '../../services/deviceService';
import { showError, showSuccess } from '../../services/toastService';
import timezones from '../../util/tz.json'

const DeviceFormModal = ({ isVisible, onClose, deviceData }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState(false);
    const [showAddNewDiv, setShowAddNewDiv] = useState(false); // State to toggle div visibility
    const [timezoneOptions, setTimezoneOptions] = useState([]);

    useEffect(() => {
        console.log(deviceData);
        if (deviceData) {
            form.setFieldsValue({
                device_id: deviceData.device_id ?? '',
                type: deviceData.type ?? '',
                device_name: deviceData.name ?? '',
                device_mac: deviceData.mac ?? '',
                device_lat: deviceData.lat ?? '',
                device_lon: deviceData.lon ?? '',
                assigned_to: deviceData.assigned_to ? parseInt(deviceData.assigned_to) : '',
            });
        }
    }, [deviceData, form]);

    const getDeviceData = async () => {
        try {
            setLoading(true);
            const device_id = form.getFieldValue('device_id');
            const type = form.getFieldValue('type');
            if (device_id.length === 0 && type.length === 0) {
                showError("Please enter device id & type")
                return false
            }
            const response = await getDeviceDetails(device_id, type)
            console.log(response)
            if (response.status === 200) {
                if (response.system_type) {
                    form.setFieldsValue({
                        name: response.type,
                        system: response.system_type,
                        mac: response.mac,
                        lat: response.lat,
                        lon: response.lon,
                    });
                } else {
                    showError("Device details not available. Please enter valid device id & type");
                }

            } else {
                form.setFieldsValue({
                    name: '',
                    system: '',
                    mac: '',
                    lat: '',
                    lon: '',
                });
            }
        } catch (error) {
            form.setFieldsValue({
                name: '',
                system: '',
                mac: '',
                lat: '',
                lon: '',
            });
            console.log(error)
            showError("Device details not available. Please enter valid device id & type");
        } finally {
            setLoading(false);
        }
    };


    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await getClients()
            const clientOptions = response.data.map(client => ({
                value: client.id, // Assuming 'id' is the unique identifier
                label: client.username // Assuming 'name' is the display name
            }));
            setCustomers(clientOptions);
        } catch (error) {
            console.error('Error fetching clients:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients()
        // Transform timezones into Select options
        const formattedTimezones = timezones.map(tz => ({
            value: tz.utc_offset,
            label: `${tz.timezone} (UTC ${tz.utc_offset})`,
        }));

        // Add "Add New" option
        setTimezoneOptions(formattedTimezones);
    }, []);


    const handleSelectChange = (value) => {
        if (value === 'add-new') {
            setShowAddNewDiv(true); // Show the div when "Add New" is selected
        } else {
            setShowAddNewDiv(false); // Hide the div for other selections
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            let response;
            if (!deviceData || !deviceData.id) {
                // Create a new client
                response = await createDevice(values);
            } else {
                // Update existing client
                response = await updateDevice(deviceData.id, values);
            }

            if (response.data.status) {
                showSuccess(response.data.message);
                form.resetFields();
                onClose();
            }
        } catch (error) {
            console.error(error);
            if (error.response) {
                switch (error.response.status) {
                    case 409:
                        showError('Client with this email/username already exists');
                        break;
                    case 400:
                        showError('Invalid data provided');
                        break;
                    case 404:
                        showError('Device not found');
                        break;
                    default:
                        showError('An unexpected error occurred');
                        break;
                }
            } else {
                showError(error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Device Information"
            open={isVisible}
            onOk={handleSubmit}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            okText={deviceData && deviceData.id ? 'Update Device' : 'Add Device'}
            cancelText="Cancel"
            confirmLoading={loading}
        >
            <Form
                form={form}
                layout="vertical"
                name="customerForm"
                initialValues={deviceData || { device_id: '', device_name: '', device_key: '', device_mac: '', device_location: '', assigned_to: '' }}
            >
                <div className="row">

                    <div className="col-12">
                        <Form.Item
                            name="assigned_to"
                            label="Customer"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select customer!',
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="Select a customer"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={
                                    customers
                                        ? customers.map(customer => ({
                                            value: customer.value, // The ID will be saved as the value
                                            label: customer.label, // This is what will be displayed
                                        })).concat([{ value: 'add-new', label: 'Add New' }])  // Add "Add New" option
                                        : [{ value: 'add-new', label: 'Add New' }]  // Fallback if customers list is empty
                                }
                                onChange={handleSelectChange}
                            />
                        </Form.Item>
                    </div>
                    {showAddNewDiv && (
                        <div className="col-12">
                            <div className="row">
                                <div className="col-12">
                                    <Form.Item
                                        name="c_name"
                                        label="Name"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input the Customer name!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter name" />
                                    </Form.Item>
                                </div>

                                <div className="col-6">
                                    <Form.Item
                                        name="email"
                                        label="Email"
                                        rules={[
                                            {
                                                required: false,
                                                message: 'Please input the email!',
                                            },
                                            {
                                                type: 'email',
                                                message: 'Please enter a valid email!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter email" type='email' />
                                    </Form.Item>
                                </div>

                                <div className="col-6">
                                    <Form.Item
                                        name="phone_number"
                                        label="Phone Number"
                                        rules={[
                                            {
                                                required: false,
                                                message: 'Please input the phone number!',
                                            },
                                            {
                                                pattern: /^[6-9]\d{9}$/,
                                                message: 'Phone number must be 10 digits and start with 6, 7, 8, or 9!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter phone number" maxLength={10} />
                                    </Form.Item>
                                </div>

                                <div className="col-6">
                                    <Form.Item
                                        name="username"
                                        label="Portal Username"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input the username!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Enter username" />
                                    </Form.Item>
                                </div>

                                <div className="col-6">
                                    <Form.Item
                                        name="password"
                                        label="Portal Password"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input the password!',
                                            },
                                        ]}
                                    >
                                        <Input.Password placeholder="Enter password" />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="col-4">
                        <Form.Item
                            name="device_id"
                            label="Device ID"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter device id!',
                                },
                            ]}
                        >
                            <Input placeholder="Device ID" />

                        </Form.Item>
                    </div>

                    <div className="col-4">
                        <Form.Item
                            name="type"
                            label="Device Type"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter type!',
                                },
                            ]}
                        >
                            <Input placeholder="Type" />

                        </Form.Item>
                    </div>

                    <div className="col-4">
                        <label>Click to fetch data</label>
                        <Button onClick={() => getDeviceData()} type="primary" loading={loading}> Get Device Details</Button>
                    </div>

                    {/* <div className="col-6">
                        <Form.Item
                            name="time_zone"
                            label="Time Zone"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter the temperature range!',
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="Select a time zone for device"
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={timezoneOptions}
                            />
                        </Form.Item>
                    </div> */}

                    <div className="col-12">
                        <Form.Item
                            name="temp_range"
                            label="Temperature Range"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter the temperature range!',
                                },
                            ]}
                        >
                            <Input placeholder="Temperature Range" />
                        </Form.Item>
                    </div>
                    <div className="col-6">
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter the device name!',
                                },
                            ]}
                        >
                            <Input placeholder="Device name" readOnly />
                        </Form.Item>
                    </div>

                    <div className="col-6">
                        <Form.Item
                            name="mac"
                            label="Device Mac Address"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter mac address!',
                                },
                            ]}
                        >
                            <Input placeholder="Enter MAC Address" readOnly />
                        </Form.Item>
                    </div>

                    <div className="col-6">
                        <Form.Item
                            name="lat"
                            label="Device Latitude"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter device lat!',
                                },
                            ]}
                        >
                            <Input placeholder="Enter Lat" readOnly />
                        </Form.Item>
                    </div>

                    <div className="col-6">
                        <Form.Item

                            name="lon"
                            label="Device Longitude"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter device lon!',
                                },
                            ]}
                        >
                            <Input placeholder="Enter Lon" readOnly />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="system"
                        label="system"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter system!',
                            },
                        ]}
                        hidden
                    >
                        <Input placeholder="Enter Lon" />
                    </Form.Item>

                </div>
            </Form>
        </Modal >
    );
};

export default DeviceFormModal;
